/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import MainLayout from "../../shared/components/MainLayout";
import axios, { AxiosResponse } from "axios";
import Loader from "../../shared/components/loader/Loader";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaSortDown,
  FaSortUp,
  FaRegQuestionCircle,
  FaUser,
  FaEnvelope,
  FaCogs,
  FaCheckCircle,
  FaRegMoneyBillAlt,
  FaCalendarAlt,
  FaRegClock,
} from "react-icons/fa"; // Importiamo icone moderne
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon, EyeIcon } from "lucide-react";
import Button2 from "../../shared/components/button/Button2";
// Definizione dell'interfaccia per i dettagli della sottoscrizione
interface SubscriptionDetails {
  customerEmail: string;
  customerName: string;
  status: string;
  product: string;
  cancellationDate: string | null;
  cancellationReason: string;
  priceMonthly: string;
  priceAnnual: string;
  createdDate: string;
}

interface ClientDetails {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  status: string;
}

// interface ApiClientResponse {
//     data: ClientDetails[];
// }

// Definizione del tipo di risposta che contiene le sottoscrizioni
interface ApiResponse {
  [x: string]: any;
  data: {
    subscriptions: SubscriptionDetails[]; // Le sottoscrizioni sono una proprietà all'interno di "data"
  };
}

const MonitorSubscription: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemPerPage] = useState<number>(10);
  const [clients, setClients] = useState<ClientDetails[]>([]);

  const [nameFilter, setNameFilter] = useState<string>("");
  const [emailFilter, setEmailFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [filteredClients, setFilteredClients] = useState<ClientDetails[]>([]);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<"createdAt" | null>(
    "createdAt"
  );

  const navigate = useNavigate();

  const sortClients = useCallback(() => {
    const sortedData = [
      ...(filteredClients.length > 0 ? filteredClients : clients),
    ];

    sortedData.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      if (sortDirection === "asc") {
        return dateA.getTime() - dateB.getTime(); // Ordinamento crescente
      } else {
        return dateB.getTime() - dateA.getTime(); // Ordinamento decrescente
      }
    });

    setFilteredClients(sortedData); // Imposta i dati ordinati
  }, [clients, filteredClients, sortDirection]);

  const handleSortClick = () => {
    setSortDirection((prevDirection) =>
      prevDirection === "asc" ? "desc" : "asc"
    );
    sortClients(); // Applica l'ordinamento
  };

  const fetchSubscriptions = async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL; // Configurazione API
    const url = `${baseUrl}/nexum-saas/monitorPurchase`; // Endpoint API
    const token = localStorage.getItem("token"); // Recupera token dall'archivio locale

    try {
      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        : {
            "Content-Type": "application/json",
          };

      const body = {
        path: "subscription", // Body della richiesta POST
      };
      // Modifica il tipo di risposta per riflettere correttamente la struttura
      const response: AxiosResponse<ApiResponse> = await axios.post(url, body, {
        headers,
      });
      //console.log("sub", response.data.subscriptions);
      // Aggiungi controllo per verificare se la risposta è corretta
      if (response.data && Array.isArray(response.data.subscriptions)) {
        setSubscriptions(response.data.subscriptions); // Imposta i dati se validi
      } else {
        setError("Dati non validi ricevuti dal server.");
      }
    } catch (err) {
      //console.error("Errore durante il recupero dei dati:", err);
      throw new Error("Failed to retrieve data");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const url = `${baseUrl}/nexum-saas/monitorPurchase`; // Endpoint API per i clienti
    const token = localStorage.getItem("token");

    try {
      const headers = token
        ? {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          }
        : {
            "Content-Type": "application/json",
          };
      const body = {
        path: "client", // Body della richiesta POST
      };

      const response: AxiosResponse<ClientDetails[]> = await axios.post(
        url,
        body,
        { headers }
      );
      if (response.data && Array.isArray(response.data)) {
        setClients(response.data); // Imposta i clienti ricevuti
      } else {
        setError("Dati non validi ricevuti dal server.");
      }
    } catch (err) {
      //console.error("Errore durante il recupero dei dati:", err);
      throw new Error("Failed to retrieve data");
    } finally {
      setLoading(false);
    }
  };

  const goToClientDetail = (clientId: string) => {
    navigate("/private/client-detail", { state: { clientId } });
  };

  const applyFilters = () => {
    let filteredData = [...clients];
    if (nameFilter) {
      filteredData = filteredData.filter((client) =>
        client.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }
    if (emailFilter) {
      filteredData = filteredData.filter((client) =>
        client.email.toLowerCase().includes(emailFilter.toLowerCase())
      );
    }
    if (statusFilter) {
      filteredData = filteredData.filter((client) =>
        client.status.toLowerCase().includes(statusFilter.toLowerCase())
      );
    }
    setFilteredClients(filteredData);
    setCurrentPage(1); // Reset della pagina ai risultati filtrati
  };

  useEffect(() => {
    // fetchSubscriptions();
    fetchClients();
  }, []);

  useEffect(() => {
    applyFilters(); // Applica i filtri ogni volta che cambiano
  }, [nameFilter, emailFilter, statusFilter]);

  useEffect(() => {
    sortClients();
  }, []);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentSubscriptions = subscriptions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const currentData = filteredClients.length > 0 ? filteredClients : clients;
  const currentClients = currentData.slice(indexOfFirstItem, indexOfLastItem);
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <MainLayout>
        <div className="text-center text-red-500 font-semibold">{error}</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Clients </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          View clients details .
        </p>
      </div>

      <div className="mb-6 flex justify-between items-center ml-10">
        <div className="flex space-x-4">
          <input
            type="text"
            placeholder="Search by Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md"
          />
          <input
            type="text"
            placeholder="Search by Email"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md"
          />
        </div>
        {/* <Button2
                    title="Search"
                    onClick={applyFilters}
                    isLoading={loading}
                    disabled={loading}
                    className="self-end px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition mr-60"
                /> */}
      </div>

      {/* Tabella dei clienti */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-lg ml-10 mr-10">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Clients</h2>
        <table className="table-auto w-full text-sm text-gray-700">
          <thead className="bg-blue-100">
            <tr>
              <th className="px-4 py-3 text-left">
                <div className="flex items-center">
                  <FaUser className="mr-2" /> Customer Name
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="flex items-center">
                  <FaEnvelope className="mr-2" /> Email
                </div>
              </th>
              <th
                className="px-4 py-3 text-left cursor-pointer"
                onClick={handleSortClick}
              >
                <div className="flex items-center">
                  <FaCalendarAlt className="mr-2" /> Created Date
                  {sortedColumn === "createdAt" &&
                    (sortDirection === "asc" ? (
                      <FaSortUp className="w-4 h-4 ml-1 text-gray-600 transition-transform duration-200 transform" />
                    ) : (
                      <FaSortDown className="w-4 h-4 ml-1 text-gray-600 transition-transform duration-200 transform" />
                    ))}
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="flex items-center">
                  <FaCheckCircle className="mr-2" /> Status
                </div>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="flex items-center">
                  <FaRegQuestionCircle className="mr-2" />
                  Details
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentClients.length > 0 ? (
              currentClients.map((client, index) => (
                <tr
                  key={index}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="px-4 py-3">{client.name}</td>
                  <td className="px-4 py-3">{client.email}</td>
                  <td className="px-4 py-3">{client.createdAt}</td>
                  <td className="px-4 py-3">{client.status}</td>
                  <td className="px-4 py-3">
                    {/* Icona interattiva per visualizzare i dettagli */}
                    <button
                      onClick={() => goToClientDetail(client.id)} // Naviga alla pagina del cliente
                      className="p-2 rounded-full bg-green-500 text-white hover:bg-blue-700 transition duration-300 ease-in-out transform hover:scale-110"
                    >
                      <EyeIcon className="h-6 w-6" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center text-gray-500 py-4">
                  No clients found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginazione dei clienti */}
      <div className="flex justify-center mt-6">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300"
        >
          Prev
        </button>
        <span className="px-4 py-2">{currentPage}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage * itemPerPage >= clients.length}
          className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300"
        >
          Next
        </button>
      </div>
      {/* User subscriptions */}
      {/* <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">User subscriptions </h1>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                    View details of your subscriptions, including user, product, duration, and status.
                </p>
            </div>
            <div className="overflow-x-auto bg-white rounded-lg shadow-lg ml-2 mr-2"> 
                <table className="table-auto w-full text-sm text-gray-700">
                    <thead className="bg-blue-100">
                        <tr>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaUser className="mr-2" /> Customer Name
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaEnvelope className="mr-2" /> Email
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaCogs className="mr-2" /> Product
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaCheckCircle className="mr-2" /> Status
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaRegMoneyBillAlt className="mr-2" /> Price (Monthly)
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaRegMoneyBillAlt className="mr-2" /> Price (Annual)
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaCalendarAlt className="mr-2" /> Created Date
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaRegClock className="mr-2" /> Cancellation Date
                                </div>
                            </th>
                            <th className="px-4 py-3 text-left">
                                <div className="flex items-center">
                                    <FaRegQuestionCircle className="mr-2" /> Cancellation Reason
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentSubscriptions.length > 0 ? (
                            currentSubscriptions.map((subscription, index) => (
                                <tr key={index} className="border-t border-gray-200 hover:bg-gray-50">
                                    <td className="px-4 py-3">{subscription.customerName}</td>
                                    <td className="px-4 py-3">{subscription.customerEmail}</td>
                                    <td className="px-4 py-3">{subscription.product}</td>
                                    <td className="px-4 py-3">{subscription.status}</td>
                                    <td className="px-4 py-3">{subscription.priceMonthly} €</td>
                                    <td className="px-4 py-3">{subscription.priceAnnual} €</td>
                                    <td className="px-4 py-3">{subscription.createdDate}</td>
                                    <td className="px-4 py-3">{subscription.cancellationDate || "N/A"}</td>
                                    <td className="px-4 py-3">{subscription.cancellationReason}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={10} className="text-center text-gray-500 py-4">
                                    No subscriptions found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div> */}

      {/* Paginazione */}
      {/* <div className="flex justify-center mt-6">
                <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300"
                >
                    Prev
                </button>
                <span className="px-4 py-2">{currentPage}</span>
                <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage * itemPerPage >= subscriptions.length}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md disabled:bg-gray-300"
                >
                    Next
                </button>
            </div> */}
    </MainLayout>
  );
};

export default MonitorSubscription;
